import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUserStore, useIsAdmin } from '../../stores/userStore';
import Logo from '../Logo';
import UserAvatar from '../UserAvatar';
import OnlineStatusChip from '../OnlineStatusChip';
import SyncStatusIndicator, { SyncQueueDrawer } from '../SyncStatusIndicator';
import ThemeToggle from '../ThemeToggle';
import MqttStatusIndicator from './MqttStatusIndicator';
import useOnlineStatus from '../../hooks/useOnlineStatus';

const navItems = [
  { label: 'navigation:work', path: '/work' },
  { label: 'navigation:scripts', path: '/scripts' }
];

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isOnline = useOnlineStatus();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [syncDrawerOpen, setSyncDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { userName, fullName, employeeCompany } = useUserStore();
  const isAdmin = useIsAdmin();
  const { logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileNavClick = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/settings/profile');
  };

  const handleSettings = () => {
    handleMenuClose();
    navigate('/settings/application');
  };

  const handleAdmin = () => {
    handleMenuClose();
    navigate('/settings/administration');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleMenuClose();
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme: { zIndex: { drawer: number } }) =>
            theme.zIndex.drawer + 1
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 2,
                minWidth: 44,
                minHeight: 44,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                },
                '&:active': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Logo />

          <Box sx={{ ml: 4, display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            {navItems.map(item => (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  color:
                    location.pathname === item.path
                      ? 'primary.main'
                      : 'text.primary',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  position: 'relative',
                  border: 'none',
                  outline: 'none',
                  '&:focus': {
                    outline: 'none',
                    border: 'none'
                  },
                  '&:active': {
                    outline: 'none',
                    border: 'none'
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    bgcolor: 'primary.main',
                    transform:
                      location.pathname === item.path
                        ? 'scaleX(1)'
                        : 'scaleX(0)',
                    transition: 'transform 0.2s'
                  }
                }}
              >
                {t(item.label)}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* User info with dropdown menu */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 2 }
            }}
          >
            <OnlineStatusChip />
            <MqttStatusIndicator />
            <SyncStatusIndicator onTap={() => setSyncDrawerOpen(true)} />
            <UserAvatar
              name={fullName || userName || 'IndySoft User'}
              subtitle={employeeCompany}
              showName={!isMobile}
              size={isMobile ? 'small' : 'medium'}
              showDropdownArrow={true}
              isClickable={true}
              onClick={handleMenuOpen}
            />
          </Box>

          {/* User Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onKeyDown={handleKeyDown}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                minWidth: 200,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                border: '1px solid',
                borderColor: 'divider'
              }
            }}
          >
            <MenuItem
              onClick={handleProfile}
              sx={{
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem
              onClick={handleSettings}
              sx={{
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            {isAdmin && (
              <MenuItem
                onClick={handleAdmin}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon>
                  <AdminIcon fontSize="small" />
                </ListItemIcon>
                Admin
              </MenuItem>
            )}
            <Divider sx={{ my: 1 }} />
            <MenuItem
              onClick={handleLogout}
              disabled={!isOnline}
              sx={{
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 250,
              height: '100%'
            }
          }}
        >
          <Box
            sx={{
              width: 250,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
                py: 2
              }}
            >
              <Logo />
              <IconButton
                onClick={handleDrawerToggle}
                sx={{ color: 'text.primary' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <List>
                {navItems.map(item => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      onClick={() => handleMobileNavClick(item.path)}
                      selected={location.pathname === item.path}
                      sx={{
                        minHeight: 48,
                        px: 3,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.dark'
                          }
                        },
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        },
                        '&:active': {
                          backgroundColor: 'action.selected'
                        }
                      }}
                    >
                      <ListItemText primary={t(item.label)} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {t('navigation:theme')}
              </Box>
              <ThemeToggle />
            </Box>
          </Box>
        </Drawer>
      </Box>

      <SyncQueueDrawer
        open={syncDrawerOpen}
        onClose={() => setSyncDrawerOpen(false)}
      />
    </>
  );
};

export default Navbar;
